import React, { useState } from 'react';
import { PhotoProvider } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import DocumentPreview, { previewHint } from './DocumentPreview';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const buildDocUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${API_URL}${path}`;
};

const DocumentModal = ({ seller, onClose, onVerify, onReject }) => {
  // Reject asks for an optional reason, which is included in the seller's email.
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const idDocUrl = buildDocUrl(seller?.id_document_url);
  const selfieUrl = buildDocUrl(seller?.selfie_url);
  const proofDocUrl = buildDocUrl(seller?.proof_of_address_url);
  const bankDocUrl = buildDocUrl(seller?.bank_confirmation_url);
  const shop = seller?.school_shop;
  const registrationLetterUrl = buildDocUrl(shop?.registration_letter_url);
  const consentLetterUrl = buildDocUrl(shop?.consent_letter_url);
  const shopBankLetterUrl = buildDocUrl(shop?.bank_confirmation_url);
  const principalIdUrl = buildDocUrl(shop?.principal_id_document_url);
  const publicSchool = shop?.school_type === 'public';

  if (!seller) return null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        maxWidth: '1100px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '15px',
            right: '15px',
            backgroundColor: '#e74c3c',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            fontSize: '20px',
            cursor: 'pointer',
            zIndex: 10
          }}
        >
          ×
        </button>

        {/* Header */}
        <div style={{ padding: '30px', borderBottom: '1px solid #eee' }}>
          <h2 style={{ margin: '0 0 15px 0', color: '#2c3e50' }}>Seller Verification — Complete Details</h2>

          {/* Personal Information */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '16px', color: '#34495e', marginBottom: '10px', borderBottom: '2px solid #3498db', paddingBottom: '5px' }}>
              Personal Information
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', color: '#666', fontSize: '14px' }}>
              <div><strong>Name:</strong> {seller.first_name} {seller.last_name}</div>
              <div><strong>Email:</strong> {seller.email}</div>
              <div><strong>Phone:</strong> {seller.phone || 'Not provided'}</div>
              <div>
                <strong>{seller.id_type === 'PASSPORT' ? `Passport (${seller.id_country || '?'})` : 'SA ID Number'}:</strong>{' '}
                {seller.id_number ? (
                  <span style={{ backgroundColor: '#fff3cd', padding: '2px 6px', borderRadius: '3px', fontFamily: 'monospace' }}>
                    {seller.id_number}
                  </span>
                ) : 'Not provided'}
              </div>
              <div><strong>School:</strong> {seller.school_name || 'Not provided'}</div>
              <div><strong>Submitted:</strong> {formatDate(seller.created_at)}</div>
            </div>
          </div>

          {/* Address Information */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '16px', color: '#34495e', marginBottom: '10px', borderBottom: '2px solid #3498db', paddingBottom: '5px' }}>
              Address Information
            </h3>
            <div style={{ color: '#666', fontSize: '14px' }}>
              {seller.street_address && (
                <div style={{ marginBottom: '5px' }}>
                  <strong>Street Address:</strong> {seller.street_address}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                <div><strong>Suburb:</strong> {seller.suburb || 'Not provided'}</div>
                <div><strong>Town:</strong> {seller.town || 'Not provided'}</div>
                <div><strong>Province:</strong> {seller.province || 'Not provided'}</div>
              </div>
            </div>
          </div>

          {/* Banking Details */}
          {(seller.bank_name || seller.bank_account_number) && (
            <div style={{ marginBottom: '10px' }}>
              <h3 style={{ fontSize: '16px', color: '#34495e', marginBottom: '10px', borderBottom: '2px solid #27ae60', paddingBottom: '5px' }}>
                Banking Details
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', color: '#666', fontSize: '14px' }}>
                <div><strong>Bank:</strong> {seller.bank_name || 'Not provided'}</div>
                <div><strong>Account Type:</strong> {seller.bank_account_type || 'Not provided'}</div>
                <div>
                  <strong>Account Number:</strong>{' '}
                  {seller.bank_account_number ? (
                    <span style={{ backgroundColor: '#d4edda', padding: '2px 6px', borderRadius: '3px', fontFamily: 'monospace' }}>
                      {seller.bank_account_number}
                    </span>
                  ) : 'Not provided'}
                </div>
                <div><strong>Branch Code:</strong> {seller.bank_branch_code || 'Not provided'}</div>
              </div>
              {seller.bank_details_confirmed_at && (
                <div style={{ marginTop: '8px', fontSize: '13px', color: '#7f8c8d' }}>
                  Seller confirmed these details are correct on {new Date(seller.bank_details_confirmed_at).toLocaleString('en-ZA')}
                </div>
              )}
            </div>
          )}

          {/* School shop application */}
          {shop && (
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '16px', color: '#34495e', marginBottom: '10px', borderBottom: '2px solid #A020C0', paddingBottom: '5px' }}>
                School Shop
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', color: '#666', fontSize: '14px' }}>
                <div><strong>School:</strong> {shop.school_name}</div>
                <div>
                  <strong>Type:</strong>{' '}
                  {shop.school_type === 'public' ? 'Public school' : shop.school_type === 'independent' ? 'Private (independent) school' : 'Not provided'}
                </div>
                <div>
                  <strong>EMIS number:</strong>{' '}
                  {shop.emis_number ? (
                    <span style={{ backgroundColor: '#fff3cd', padding: '2px 6px', borderRadius: '3px', fontFamily: 'monospace' }}>
                      {shop.emis_number}
                    </span>
                  ) : 'Not provided'}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', color: '#666', fontSize: '14px', marginTop: '10px' }}>
                <div>
                  <strong>Principal / SGB chair {shop.principal_id_type === 'PASSPORT' ? `passport (${shop.principal_id_country || '?'})` : 'SA ID number'}:</strong>{' '}
                  {shop.principal_id_number ? (
                    <span style={{ backgroundColor: '#fff3cd', padding: '2px 6px', borderRadius: '3px', fontFamily: 'monospace' }}>
                      {shop.principal_id_number}
                    </span>
                  ) : 'Not provided'}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', color: '#666', fontSize: '14px', marginTop: '10px' }}>
                <div><strong>Account holder:</strong> {shop.bank_account_holder || 'Not provided'}</div>
                <div><strong>Bank:</strong> {shop.bank_name || 'Not provided'} {shop.bank_account_type ? `(${shop.bank_account_type})` : ''}</div>
                <div>
                  <strong>Account Number:</strong>{' '}
                  {shop.bank_account_number ? (
                    <span style={{ backgroundColor: '#d4edda', padding: '2px 6px', borderRadius: '3px', fontFamily: 'monospace' }}>
                      {shop.bank_account_number}
                    </span>
                  ) : 'Not provided'}
                </div>
                <div><strong>Branch Code:</strong> {shop.bank_branch_code || 'Not provided'}</div>
              </div>
              <div style={{ marginTop: '8px', fontSize: '13px', color: '#7f8c8d' }}>
                Check the bank letter shows this account in the school's own name, and the principal's ID matches the ID number and the consent letter's signatory.
                Check the letter names this school (and EMIS number), and the consent letter is signed by the principal or
                governing body chairperson and names {seller.first_name} {seller.last_name}.
              </div>
            </div>
          )}

          {/* Verification Status */}
          <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            <strong>Verification Status:</strong>{' '}
            <span style={{
              padding: '4px 10px',
              borderRadius: '4px',
              backgroundColor: '#fff3cd',
              color: '#856404',
              fontWeight: 'bold'
            }}>
              {seller.verification_status}
            </span>
          </div>
        </div>

        {/* Documents — 3 columns */}
        <div style={{ padding: '30px' }}>
          <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#2c3e50' }}>Uploaded Documents</h3>

          <PhotoProvider>
            {/* Applications upload an ID and type in the address and bank details. Older ones also
                uploaded a proof of address and bank letter; a few from 2026-10-03 have a selfie. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
              <div>
                <h4 style={{ marginBottom: '10px', color: '#34495e' }}>ID Document</h4>
                <DocumentPreview url={idDocUrl} label="ID Document" />
                <p style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '5px' }}>
                  {previewHint(idDocUrl)}
                </p>
              </div>

              {selfieUrl && <div>
                <h4 style={{ marginBottom: '10px', color: '#34495e' }}>Selfie</h4>
                <DocumentPreview url={selfieUrl} label="Selfie" />
                <p style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '5px' }}>
                  Check the face matches the ID photo
                </p>
              </div>}

              {proofDocUrl && <div>
                <h4 style={{ marginBottom: '10px', color: '#34495e' }}>Proof of Address</h4>
                <DocumentPreview url={proofDocUrl} label="Proof of Address" />
                <p style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '5px' }}>
                  {previewHint(proofDocUrl)}
                </p>
              </div>}

              {shop && <div>
                <h4 style={{ marginBottom: '10px', color: '#34495e' }}>Principal / SGB Chair ID</h4>
                <DocumentPreview url={principalIdUrl} label="Principal ID" />
                <p style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '5px' }}>
                  {previewHint(principalIdUrl)}
                </p>
              </div>}

              {shop && <div>
                <h4 style={{ marginBottom: '10px', color: '#34495e' }}>
                  {publicSchool ? 'Dept. of Education Confirmation Letter' : 'School Registration Letter'}
                </h4>
                <DocumentPreview url={registrationLetterUrl} label="School registration" />
                <p style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '5px' }}>
                  {previewHint(registrationLetterUrl)}
                </p>
              </div>}

              {shop && <div>
                <h4 style={{ marginBottom: '10px', color: '#34495e' }}>Principal / SGB Consent Letter</h4>
                <DocumentPreview url={consentLetterUrl} label="Consent letter" />
                <p style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '5px' }}>
                  {previewHint(consentLetterUrl)}
                </p>
              </div>}

              {shop && <div>
                <h4 style={{ marginBottom: '10px', color: '#34495e' }}>School Bank Confirmation Letter</h4>
                <DocumentPreview url={shopBankLetterUrl} label="School bank confirmation" />
                <p style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '5px' }}>
                  {previewHint(shopBankLetterUrl)}
                </p>
              </div>}

              {bankDocUrl && <div>
                <h4 style={{ marginBottom: '10px', color: '#34495e' }}>Bank Confirmation Letter</h4>
                <DocumentPreview url={bankDocUrl} label="Bank Confirmation" />
                <p style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '5px' }}>
                  {previewHint(bankDocUrl)}
                </p>
              </div>}
            </div>
          </PhotoProvider>
        </div>

        {/* Action Buttons */}
        {rejecting ? (
          <div style={{ padding: '20px 30px', borderTop: '1px solid #eee' }}>
            <label htmlFor="reject-reason" style={{ display: 'block', fontWeight: 'bold', color: '#2c3e50', marginBottom: '8px' }}>
              Reason for rejection (optional — included in the email to the seller)
            </label>
            <textarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              maxLength={1000}
              rows={3}
              placeholder="e.g. The ID photo is blurry — please upload a clearer copy."
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '14px', fontFamily: 'inherit' }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '12px' }}>
              <button
                onClick={() => { setRejecting(false); setRejectReason(''); }}
                style={{ padding: '10px 20px', backgroundColor: '#95a5a6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
              >
                Back
              </button>
              <button
                onClick={() => onReject(seller.id, rejectReason.trim())}
                style={{ padding: '10px 20px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
              >
                Reject &amp; email seller
              </button>
            </div>
          </div>
        ) : (
        <div style={{
          padding: '20px 30px',
          borderTop: '1px solid #eee',
          display: 'flex',
          gap: '10px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: '#95a5a6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => setRejecting(true)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#e74c3c',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Reject
          </button>
          <button
            onClick={() => {
              if (window.confirm('Are you sure you want to VERIFY this seller? They will be able to list items for sale, and will be emailed.')) {
                onVerify(seller.id);
              }
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#27ae60',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Verify
          </button>
        </div>
        )}
      </div>
    </div>
  );
};

export default DocumentModal;
